import { Info } from '@mui/icons-material';
import { Box, Stack, Tooltip } from '@mui/material';
import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { InterventionPeriodNimReadDTO } from '../../api/dto';
import { FilterTypeEnum } from '../../components/searching/filters';
import { fetchInterventionPeriodNames, fetchInterventionPeriodsByFacility } from '../../lib/api';
import { formatDate } from '../../lib/date';
import { translateValue } from '../../lib/language';
import { AsyncAutocompleteFetchOptions, FetchUseCase } from '../fields/AsyncAutocompleteElement';
import { AsyncAutocompleteFormElement, TFilterElement, TFormElement } from '../types';
import { UiSchemaType } from '../UiSchemaType';

interface InterventionPeriodTypePropertiesParameters {
    multiple?: boolean;
    fetchEndpoint?: AsyncAutocompleteFetchOptions<InterventionPeriodNimReadDTO>;
    watchPaths?: string[];
    fetchUseCases?: FetchUseCase[];
}

const interventionPeriodTypeProperties = ({
    multiple,
    fetchEndpoint,
    watchPaths,
    fetchUseCases,
}: InterventionPeriodTypePropertiesParameters): AsyncAutocompleteFormElement['typeProperties'] => ({
    fetchOptions: fetchEndpoint ?? fetchInterventionPeriodsByFacility,
    watchPaths: watchPaths ?? ['facility'],
    fetchUseCases: fetchUseCases ?? [FetchUseCase.ON_OPEN],
    getOptionLabel: (value) => value.periodName,
    multiple,
    endAdornment: ({ field, disabled, readOnly }) => {
        const intl = useIntl();
        const formatInterventionPeriod = (interventionPeriod: InterventionPeriodNimReadDTO) => {
            const lines: { label: MessageDescriptor['id']; value: string }[] = [
                {
                    label: 'document.field.period',
                    value: `${formatDate(interventionPeriod.periodStart, intl)} - ${formatDate(
                        interventionPeriod.periodEnd,
                        intl
                    )}`,
                },
                {
                    label: 'document.field.type',
                    value: translateValue(interventionPeriod.periodType, intl.locale).label,
                },
                {
                    label: 'document.field.facility',
                    value: translateValue(interventionPeriod.interventionFacility, intl.locale).label,
                },
            ];
            return (
                <Stack direction="column">
                    {lines.map(({ label, value }, i) => (
                        <Box key={i}>
                            {intl.formatMessage(
                                { id: 'common.labelValue' },
                                { label: intl.formatMessage({ id: label }), value }
                            )}
                        </Box>
                    ))}
                </Stack>
            );
        };
        const { value: interventionPeriod } = field;
        return (
            <>
                {!multiple && interventionPeriod && !Array.isArray(interventionPeriod) ? (
                    <Tooltip title={formatInterventionPeriod(interventionPeriod)}>
                        <Info
                            color={!disabled ? 'action' : 'disabled'}
                            sx={{ cursor: 'help', mr: !readOnly ? 0 : 4 }}
                        />
                    </Tooltip>
                ) : null}
            </>
        );
    },
});

interface InterventionPeriodSchemaParameters
    extends InterventionPeriodTypePropertiesParameters,
        Pick<AsyncAutocompleteFormElement, 'path' | 'label' | 'required'> {}

export const interventionPeriodSchema = ({
    path,
    label,
    multiple,
    required,
}: InterventionPeriodSchemaParameters): TFormElement => ({
    type: UiSchemaType.AUTOCOMPLETE_ASYNC,
    path,
    label,
    required,
    typeProperties: interventionPeriodTypeProperties({ multiple }),
});

interface InterventionPeriodFilterSchemaParameters extends Pick<TFilterElement, 'id' | 'label'> {
    fetchEndpoint?: AsyncAutocompleteFetchOptions<InterventionPeriodNimReadDTO>;
    watchPaths?: string[];
    fetchUseCases?: FetchUseCase[];
}

export const interventionPeriodFilterSchema = ({
    id,
    label,
    fetchEndpoint,
    watchPaths,
    fetchUseCases,
}: InterventionPeriodFilterSchemaParameters): TFilterElement => ({
    type: FilterTypeEnum.AUTOCOMPLETE_ASYNC,
    id,
    label,
    valueGetter: (value) => value.periodName,
    typeProperties: {
        ...interventionPeriodTypeProperties({
            fetchEndpoint: fetchEndpoint ?? fetchInterventionPeriodNames,
            watchPaths,
            fetchUseCases: fetchUseCases ?? [FetchUseCase.ON_KEYSTROKE],
        }),
        autocompleteProps: {
            disableCloseOnSelect: false,
            forcePopupIcon: false,
        },
    },
});
